import React, { useState, useEffect } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { LogOut, LayoutDashboard, Package, ChefHat, Settings, BarChart3, Shield, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OnboardingModal } from '@/components/OnboardingModal';
import { TrialBanner } from '@/components/SubscriptionBanners';

export const ProtectedLayout = () => {
  const { user, logout, loading } = useAuth();
  const { subscription, isAdmin, refresh } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Refresh subscription when layout mounts
    if (user) {
      refresh();
    }
  }, [user]);

  useEffect(() => {
    // Show onboarding if not completed
    if (subscription && !subscription.onboarding_completed) {
      setShowOnboarding(true);
    }
  }, [subscription]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Ingredients', path: '/ingredients', icon: Package },
    { name: 'Recipes', path: '/recipes', icon: ChefHat },
    { name: 'Analytics', path: '/analytics', icon: BarChart3 },
    { name: 'Settings', path: '/settings', icon: Settings },
  ];

  // Add admin link if user is admin
  if (isAdmin) {
    navItems.push({ name: 'Admin', path: '/admin', icon: Shield });
  }

  const currentPath = window.location.pathname;

  // Get plan badge color
  const getPlanBadgeStyle = () => {
    const planName = subscription?.plan_name || subscription?.tier_name || 'Starter';
    switch (planName) {
      case 'Pro Baker': return 'bg-purple-500 text-white';
      case 'Home Baker': return 'bg-[#2C5F7C] text-white';
      case 'Baker Lite': return 'bg-green-500 text-white';
      default: return 'bg-[#EFF6FF] text-[#2C5F7C]';
    }
  };

  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      {/* Banners */}
      <TrialBanner />
      
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-white border-b border-[#DBEAFE]">
        <div className="flex items-center gap-2">
          <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-8 h-8 rounded-md object-contain" />
          <h1 className="text-xl font-bold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            CostCraft
          </h1>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 hover:bg-[#EFF6FF] rounded-lg transition-colors"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6 text-[#2C5F7C]" />
          ) : (
            <Menu className="w-6 h-6 text-[#2C5F7C]" />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="flex-1 flex">
        {/* Sidebar - Hidden on mobile, shown as slide-over when menu open */}
        <aside 
          className={`
            fixed md:relative inset-y-0 left-0 z-50
            w-64 bg-white border-r border-[#DBEAFE] flex flex-col
            transform transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            md:translate-x-0
          `}
          data-testid="sidebar"
        >
          {/* Mobile close button in sidebar */}
          <div className="md:hidden flex justify-end p-2">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 hover:bg-[#EFF6FF] rounded-lg"
            >
              <X className="w-5 h-5 text-[#6B7280]" />
            </button>
          </div>

          <div className="p-6 border-b border-[#DBEAFE]">
            <div className="hidden md:flex items-center gap-2 mb-1">
              <img src="/costcraft-logo.jpg" alt="CostCraft" className="w-9 h-9 rounded-md object-contain" />
              <h1 className="text-2xl font-bold text-[#2C5F7C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CostCraft
              </h1>
            </div>
            <p className="text-sm text-[#6B7280] mt-1 truncate">{user.email}</p>
            {subscription && (
              <div className="mt-2">
                <span className={`text-xs px-2 py-1 rounded-full ${getPlanBadgeStyle()}`}>
                  {subscription.plan_name || subscription.tier_name || 'Starter'}
                  {subscription.is_trial && ' (Trial)'}
                </span>
              </div>
            )}
          </div>

          <nav className="flex-1 p-4 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath.startsWith(item.path);
              const isAdminNav = item.name === 'Admin';
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-colors ${
                    isActive
                      ? isAdminNav ? 'bg-purple-600 text-white' : 'bg-[#2C5F7C] text-white'
                      : isAdminNav ? 'text-purple-600 hover:bg-purple-50' : 'text-[#6B7280] hover:bg-[#EFF6FF]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </button>
              );
            })}
          </nav>

          <div className="p-4 border-t border-[#DBEAFE]">
            <Button
              onClick={handleLogout}
              data-testid="logout-button"
              variant="outline"
              className="w-full h-11 rounded-full border-[#DBEAFE] hover:bg-[#F3E8EC]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden">
          <div className="max-w-7xl mx-auto p-4 md:p-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && (
        <OnboardingModal onClose={() => setShowOnboarding(false)} />
      )}
    </div>
  );
};